import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { TIME_FORMAT_HOURS_MINUTES, DATE_TIME_FORMAT_MED } from '../../utils/dates';
import Chevron from '../../atoms/Chevron';
import StatusBadge from '../../atoms/StatusBadge/StatusBadge';
import DeviceDataTable from './DeviceDataTable';
import DeviceFaultTable from './DeviceFaultTable';
import BinControllerSensorStatusSection from './BinControllerSensorStatusSection';
import DeviceConfigSection from './DeviceConfigSection';
import DeviceCommandsSection from './DeviceCommandsSection';
import './DeviceCard.scss';
import CopyButton from '../../atoms/CopyButton';

function CollapsingSection({ title, children, open = false, onOpen = () => { } }) {
  const [isOpen, setOpen] = useState(open);

  return (
    <div className="section">
      <div
        className="clickArea noselect"
        onClick={() => {
          setOpen(!isOpen);
          onOpen(isOpen);
        }}
      >
        <Chevron className="DeviceCard-collapsingSectionChevron" open={isOpen} />
        {title}
      </div>
      <div className="childHolder">{isOpen ? children : null}</div>
    </div>
  );
}
CollapsingSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
};

export default function DeviceCardView({
  deviceId,
  error,
  serial,
  deviceType,
  barnTitle,
  barnId = null,
  onBarnClick,
  feedLineTitle,
  onFeedLineClick,
  quickFacts,
  qaReports,
  calibrations,
  lensCalibrations = [],
  inductiveSensorTestViews = [],
  transactionChart,
  rawDataChart,
  algorithmOverrideSection,
}) {
  const quickFactsCards = quickFacts.map((q, i) => (
    <div key={i}>
      <div className="title">{q.title}</div>
      <div className="value" title={q.detail}>
        {q.value}
      </div>
    </div>
  ));
  let qaReport = qaReports?.map((qa) => {
    if (!qa.tests?.length) return null;

    const testIcons = qa.tests.map((test, index) => {
      const skipped = test.status === 'not_tested';
      const dotClass = skipped ? '' : test.error ? 'fail' : 'pass';

      return (
        <span
          key={`${test.device}-${index}`}
          title={`${test.device}: ${test.status}`}
          className={`dot ${dotClass}`.trim()}
        />
      );
    });

    const dateInMilliseconds = qa.date * 1000;
    const reportTitle = [qa.errorDetail, qa.type].filter(Boolean).join(' — ');

    return (
      <div key={qa.id} className="qaReport DeviceCard-row">
        <span
          className="date"
          title={
            reportTitle
              ? `${reportTitle} (${dayjs.tz(dateInMilliseconds).format(TIME_FORMAT_HOURS_MINUTES)})`
              : dayjs.tz(dateInMilliseconds).format(TIME_FORMAT_HOURS_MINUTES)
          }
        >
          {dayjs.tz(dateInMilliseconds).format(DATE_TIME_FORMAT_MED)}
        </span>
        {testIcons}
        <span className="status">
          <StatusBadge status={qa.status === 'pass' ? 'success' : 'error'} text={qa.status || 'blank'} />
        </span>
      </div>
    );
  });

  const calibrationViews = calibrations && calibrations.length > 0 ? calibrations.map((cal, i) => {
    const dateInMilliseconds = cal.date * 1000;
    return (
      <div key={i} className="calibration DeviceCard-row">
        <span className="date" title={dayjs.tz(dateInMilliseconds).format(TIME_FORMAT_HOURS_MINUTES)}>
          {dayjs.tz(dateInMilliseconds).format(DATE_TIME_FORMAT_MED)}
        </span>
        <span className="status" title={JSON.stringify(cal.json_body)}>
          <StatusBadge status={cal.status === 'pass' ? 'success' : 'error'} text={cal.status || 'blank'} />
        </span>
        <CopyButton data={JSON.stringify(cal.json_body, null, 4)}></CopyButton>
        <span className="text">
          <p>{cal.version}</p>
        </span>
        <span className="DeviceCard-rawDataLink">
          <Link target="_blank" to={`/rawData/${deviceId}?from=${cal.started_at}&to=${cal.ended_at}`}>
            📈
          </Link>
        </span>
      </div>
    );
  }) : [];

  qaReport = qaReport || <span>No Reports</span>;

  if (error) return <div className="DeviceCard">{JSON.stringify(error)}</div>;

  return (
    <div className="DeviceCard">
      <div className="title">
        <span className="DeviceCard-serialHeading">
          <span title={deviceId} className="serial">
            {serial}
          </span>
          <CopyButton data={deviceId != null ? String(deviceId) : ''} hoverText="Copy device ID" />
        </span>
        <span className="barn" onClick={onBarnClick}>
          {barnTitle}
        </span>
        <span className="feed_line_name" onClick={onFeedLineClick}>
          - {feedLineTitle}
        </span>
      </div>
      <CollapsingSection title="Quick Facts" open>
        <div className="quick_facts">{quickFactsCards}</div>
      </CollapsingSection>
      <CollapsingSection title="Device Configuration">
        <DeviceConfigSection deviceId={deviceId} />
      </CollapsingSection>
      <CollapsingSection title="Device Commands">
        <DeviceCommandsSection deviceId={deviceId} deviceType={deviceType} />
      </CollapsingSection>
      {deviceType === 'BinController' && (
        <CollapsingSection title="Connected Sensors">
          <BinControllerSensorStatusSection deviceId={deviceId} />
        </CollapsingSection>
      )}
      {algorithmOverrideSection && (
        <CollapsingSection title="Algorithm Override">{algorithmOverrideSection}</CollapsingSection>
      )}
      <CollapsingSection title="QA Reports">
        <div className="DeviceCard-rows">{qaReport}</div>
      </CollapsingSection>
      {deviceType === 'FeedLine' && (
        <>
          <CollapsingSection title="Calibrations">
            <div className="DeviceCard-rows">{calibrationViews}</div>
          </CollapsingSection>
          <CollapsingSection title="Lens Calibrations">
            {lensCalibrations && lensCalibrations.length > 0 ? lensCalibrations : 'No lens calibrations available'}
          </CollapsingSection>
          <CollapsingSection title="Inductive Sensor Tests">
            {inductiveSensorTestViews && inductiveSensorTestViews.length > 0
              ? inductiveSensorTestViews
              : 'No inductive sensor tests available'}
          </CollapsingSection>
        </>
      )}
      <CollapsingSection title="HTTP Trace">{transactionChart}</CollapsingSection>
      <CollapsingSection title="Sensor Data">
        <Link to={`/rawData/${deviceId}?to=${Math.floor(new Date().getTime() / 1000)}`}>📈24h</Link>
        {rawDataChart}
      </CollapsingSection>
      <CollapsingSection title="Fault Codes/Events">
        <DeviceFaultTable deviceId={deviceId} barnId={barnId} />
      </CollapsingSection>
      <CollapsingSection title="Device Data Blocks">
        <DeviceDataTable deviceId={deviceId} />
      </CollapsingSection>
    </div>
  );
}

DeviceCardView.propTypes = {
  deviceId: PropTypes.string,
  error: PropTypes.object,
  serial: PropTypes.string,
  deviceType: PropTypes.string,
  barnTitle: PropTypes.string,
  barnId: PropTypes.string,
  onBarnClick: PropTypes.func,
  feedLineTitle: PropTypes.string,
  onFeedLineClick: PropTypes.func,
  quickFacts: PropTypes.array,
  qaReports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      date: PropTypes.number,
      status: PropTypes.string,
      type: PropTypes.string,
      errorDetail: PropTypes.string,
      tests: PropTypes.arrayOf(
        PropTypes.shape({
          device: PropTypes.string,
          status: PropTypes.string,
          error: PropTypes.bool,
        })
      ),
    })
  ),
  calibrations: PropTypes.array,
  lensCalibrations: PropTypes.array,
  inductiveSensorTestViews: PropTypes.array,
  transactionChart: PropTypes.element,
  rawDataChart: PropTypes.element,
  algorithmOverrideSection: PropTypes.node,
};
